import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Group, Field } from '../types';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableField } from './SortableField';
import { Folder, Trash2, GripVertical } from 'lucide-react';
import { useEditor } from '../store/EditorContext';

interface GroupContainerProps {
  group: Group;
  fields: Field[];
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

export const GroupContainer: React.FC<GroupContainerProps> = ({ group, fields, isSelected, onSelect, onDelete }) => {
  const { state, dispatch } = useEditor();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: group.id, data: { type: 'group' } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleFieldSelect = (id: string) => {
      dispatch({ type: 'SELECT_ELEMENT', payload: { id, type: 'field' } });
  };

  const handleFieldDelete = (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch({ type: 'DELETE_FIELD', payload: id });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-slate-50 rounded-xl border transition-all mb-8 overflow-hidden shadow-sm ${
        isSelected 
          ? 'border-teal-500 ring-1 ring-teal-500/20' 
          : 'border-slate-200 hover:border-teal-300'
      }`}
      onClick={onSelect}
    >
      {/* Group Header */}
      <div className="flex items-center gap-3 p-4 border-b border-slate-200 bg-white cursor-pointer group">
        <div 
            className="text-slate-300 cursor-grab active:cursor-grabbing hover:text-slate-500"
            {...attributes}
            {...listeners}
        >
            <GripVertical size={18} />
        </div>
        
        <div className="flex items-center gap-2 text-slate-800 font-bold text-lg">
            <Folder size={20} className="text-teal-600" />
            <span>{group.label}</span>
        </div>

        <button 
            onClick={onDelete}
            className="ml-auto p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
        >
            <Trash2 size={18} />
        </button>
      </div>

      {/* Group Content (Droppable Area for Fields) */}
      <div className="p-6 min-h-[100px] space-y-4">
        <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
             <div className="space-y-4">
                {fields.map(field => (
                     <SortableField 
                        key={field.id} 
                        field={field} 
                        isSelected={state.selectedElementId === field.id && state.selectedElementType === 'field'}
                        onSelect={() => handleFieldSelect(field.id)}
                        onDelete={(e) => handleFieldDelete(field.id, e)}
                    />
                ))}
                {fields.length === 0 && (
                    <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-lg text-sm font-medium italic">
                        Drop fields here to populate group
                    </div>
                )}
             </div>
        </SortableContext>
      </div>
    </div>
  );
};